import * as React from 'react';
import {
  CianNewbuildingChatButton,
  CianNewbuildingChatButtonContext,
  TContactParam,
} from '@cian/newbuilding-chat-component';
import { IconCommunicationMessage16 } from '@cian/ui-kit-design-tokens/icons';

import { NewbuildingChatPopup } from '../../components/NewbuildingChatPopup';
import { NewbuildingChatModal } from '../../components/NewbuildingChatModal';
import { IBuilder, ISeller } from '../../types/newbuildingsCompare';
import { useApplicationContext } from '../../utils/applicationContext';
import { useAnalyticsContext } from '../../utils/analyticsContext';
import { TSellerType } from '../NewbuildingCallButton/tracking';
import { buildChatUrl } from './buildChatUrl';
import { NewbuildingChatCallComponent } from './NewbuildingChatCallComponent';
import { trackOpenChatContacts, trackOpenChatGallery, trackOpenMessagePopup, trackSendMessage } from './tracking';

type TChatContext = React.ContextType<typeof CianNewbuildingChatButtonContext>;
type TChatBuilder = TChatContext['builders'][number];
type TChatSeller = TChatContext['sellers'][number];

export interface INewbuildingChatButtonProps {
  newbuildingId: number;
  url: string;
  builders: IBuilder[];
  sellers: ISeller[];
  size: 'M' | 'XS' | 'L';
  /** Открывать чат в полноэкранной модалке (мобильная версия) вместо углового поповера. */
  fullscreen?: boolean;
}

function toChatBuilder(builder: IBuilder): TChatBuilder {
  return {
    realtyUserId: builder.realtyUserId ?? 0,
    name: builder.name,
    logoUrl: builder.logoUrl,
    showPhone: Boolean(builder.phone),
    phone: builder.phone ?? undefined,
    chat: builder.chat
      ? {
          isEnabled: builder.chat.enabled,
          defaultMessage: builder.chat.suggestText,
          entityId: builder.chat.entityId,
          context: builder.chat.context,
        }
      : undefined,
  };
}

function toChatSeller(seller: ISeller): TChatSeller {
  return {
    realtyUserId: seller.realtyUserId ?? 0,
    name: seller.name,
    isLeadFactory: false,
    showPhone: Boolean(seller.phone),
    phone: seller.phone ?? undefined,
    chat: seller.chat
      ? {
          isEnabled: seller.chat.enabled,
          defaultMessage: seller.chat.suggestText,
          entityId: seller.chat.entityId,
          context: seller.chat.context,
        }
      : undefined,
  };
}

export const NewbuildingChatButton: React.FC<INewbuildingChatButtonProps> = ({
  newbuildingId,
  url,
  builders,
  sellers,
  size,
  fullscreen = false,
}) => {
  const { config } = useApplicationContext();
  const { placement } = useAnalyticsContext();
  const chatHost = config.get<string>('builderChatHost') ?? '';

  const [chatContact, setChatContact] = React.useState<TContactParam | null>(null);
  const wasModalOpenedRef = React.useRef(false);

  const sellerTypeByRealtyUserId = React.useMemo(() => {
    const map = new Map<number, TSellerType>();
    builders.forEach(builder => map.set(builder.realtyUserId ?? 0, 'builders'));
    sellers.forEach(seller => map.set(seller.realtyUserId ?? 0, 'agents'));

    return map;
  }, [builders, sellers]);

  const CallComponent = React.useMemo<TChatContext['CallComponent']>(
    () =>
      function ChatCall(props) {
        return <NewbuildingChatCallComponent {...props} newbuildingId={newbuildingId} url={url} />;
      },
    [newbuildingId, url],
  );

  const contextValue = React.useMemo<TChatContext>(
    () => ({
      builders: builders.map(toChatBuilder),
      sellers: sellers.map(toChatSeller),
      CallComponent,
    }),
    [builders, sellers, CallComponent],
  );

  const handleContactListModalOpen = React.useCallback(() => {
    wasModalOpenedRef.current = true;
    trackOpenChatContacts({ newbuildingId, url });
  }, [newbuildingId, url]);

  const handleChatOpen = React.useCallback(
    (contact: TContactParam) => {
      const sellerType = wasModalOpenedRef.current ? sellerTypeByRealtyUserId.get(contact.realtyUserId) : undefined;
      const ownerId = contact.realtyUserId;

      // Аналитика отправляется парами, так как страница доступна только авторизованным пользователям
      if (placement === 'gallery') {
        trackOpenChatGallery({ newbuildingId, url, category: 'CompareJK', action: 'send_message' });
        trackOpenChatGallery({ newbuildingId, url, category: 'Message_popup', action: 'open' });
      } else {
        trackSendMessage({ newbuildingId, url, ownerId, sellerType });
        trackOpenMessagePopup({ newbuildingId, url, ownerId, sellerType });
      }
      setChatContact(contact);
    },
    [placement, newbuildingId, sellerTypeByRealtyUserId, url],
  );

  const handleSellersInfoClick = React.useCallback(() => {}, []);
  const handleClose = React.useCallback(() => setChatContact(null), []);

  const chatUrl = chatContact ? buildChatUrl({ chatHost, contact: chatContact, gaLabel: url }) : undefined;

  return (
    <>
      <CianNewbuildingChatButtonContext.Provider value={contextValue}>
        <CianNewbuildingChatButton
          aria-label="Написать сообщение"
          beforeIcon={<IconCommunicationMessage16 color="current_color" />}
          size={size}
          theme="main_primary"
          onChatOpen={handleChatOpen}
          onContactListModalOpen={handleContactListModalOpen}
          onSellersInfoClick={handleSellersInfoClick}
        />
      </CianNewbuildingChatButtonContext.Provider>
      {fullscreen ? (
        <NewbuildingChatModal open={Boolean(chatContact)} url={chatUrl} onClose={handleClose} />
      ) : (
        chatContact && <NewbuildingChatPopup url={chatUrl} onClose={handleClose} />
      )}
    </>
  );
};
